/**
 * What the web tools create, matching Centerline's defaults (addMarkup /
 * addMeasurement / promptForApprovalStamp / QuickMeasurePreferences).
 */
import {
  newItemId, pointsBox, type ApprovalStampDetails, type AppearanceColor, type CenterlineDocument, type Markup,
  type Measurement, type QuickLengthUnit, type QuickAreaUnit, type StampDefinition, type TextBoxDetails, type Vec2,
} from "@centerline/core/viewer";

export type ToolId = "select" | "pan" | "calibrate" | "measureDistance" | "measureArea" | "linear" | "area" | "count" | "text" | "stamp" | "cloud" | "arrow" | "highlight" | "pen" | "marker" | "pin" | "photopin" | "punchpin" | "subpin" | "detailpin" | "detailbox" | "line" | "symbol";

export const TOOLS: Array<{ id: ToolId; label: string; key: string; title: string }> = [
  { id: "select", label: "↖ Select", key: "v", title: "Select, move (drag) and delete (⌫) markups and takeoffs (V)" },
  { id: "pan", label: "✋", key: "h", title: "Pan (H)" },
  { id: "calibrate", label: "📐 Calibrate", key: "k", title: "Calibrate: click both ends of a known distance (K)" },
  { id: "measureDistance", label: "📏 Distance", key: "m", title: "Quick measure distance: click points, double-click or Enter to finish (M)" },
  { id: "measureArea", label: "⬠ Area", key: "a", title: "Quick measure area: click corners, double-click or Enter to finish (A)" },
  { id: "linear", label: "〰 Linear", key: "l", title: "Linear (polyline) takeoff: click points, double-click or Enter to finish (L)" },
  { id: "area", label: "▱ Area takeoff", key: "t", title: "Area takeoff: click corners, double-click or Enter to finish (T)" },
  { id: "count", label: "• Count", key: "c", title: "Count takeoff: click each item, Enter to finish (C)" },
  { id: "text", label: "T Text", key: "x", title: "Text box: click or drag a box, then type (X)" },
  { id: "stamp", label: "▣ Stamp", key: "s", title: "Stamp: pick a stamp, then click or drag to place (S)" },
  { id: "cloud", label: "☁ Cloud", key: "r", title: "Revision cloud: tap corners, double-tap / Done to finish (R)" },
  { id: "line", label: "╱ Line", key: "e", title: "Straight line (e.g. a pipe run): tap the start, then the end. Hold Shift to snap to 0/45/90° (E)" },
  { id: "symbol", label: "△ Symbol", key: "b", title: "Symbol: pick triangle, square or circle, then tap to place (B)" },
  { id: "arrow", label: "➚ Arrow", key: "w", title: "Arrow: tap the tail then the point (W)" },
  { id: "highlight", label: "▭ Highlight", key: "g", title: "Highlight box: drag over completed work — goes on the Completed work layer (G)" },
  { id: "pen", label: "✎ Pen", key: "p", title: "Freehand pen; Apple Pencil pressure sets the line width (P)" },
  { id: "marker", label: "🖍 Marker", key: "i", title: "Freehand highlighter; pressure sets the width (I)" },
  { id: "pin", label: "📍 Pin", key: "n", title: "Drop a pin (Submittal, RFI, Photo, Note, Punch) on a spot (N)" },
  { id: "photopin", label: "📷 Photo pin", key: "o", title: "Photo pin: tap a spot, then take a photo or pick one (O)" },
  { id: "punchpin", label: "✅ Punch", key: "j", title: "Punch item: tap the spot, describe what needs fixing, assign it (J)" },
  { id: "subpin", label: "📎 Pin submittal", key: "u", title: "Pin submittal: tap the structure, then pick pages from a submittal packet or PDF (U)" },
  { id: "detailpin", label: "⊕ Detail pin", key: "d", title: "Detail pin: tap the callout, pick the sheet the detail is on, then drag a box around it (D)" },
];

export const MULTI_POINT: ReadonlySet<ToolId> = new Set(["measureDistance", "measureArea", "linear", "area", "count", "cloud", "arrow", "line"]);
export const FREEHAND: ReadonlySet<ToolId> = new Set(["pen", "marker"]);
export const minPoints = (t: ToolId) => (t === "measureArea" || t === "area" || t === "cloud" ? 3 : t === "count" ? 1 : 2);
/** Tools that close into a polygon. */
export const CLOSED: ReadonlySet<ToolId> = new Set(["measureArea", "area", "cloud"]);

const now = () => new Date().toISOString();

export function textBox(pageId: string, layer: string, bounds: [number, number, number, number], text: string): Markup {
  const style: TextBoxDetails = {
    text, isBold: false, alignment: "left",
    style: { borderColor: "blue", borderStyle: "solid", borderWidth: 1.2, fillColor: "white", textColor: "ink", font: "helvetica", fontSize: 11 },
  };
  return { id: newItemId(), pageId, bounds, rotationDegrees: 0, layer, kind: "note", style, createdAt: now() } as Markup;
}

/** Centerline sizes a note by its text (fittedTextBounds, grow-only). */
export function fittedTextBounds(text: string, fontSize: number, at: Vec2, drag?: [number, number, number, number]): [number, number, number, number] {
  const lines = text.split("\n");
  const longest = Math.max(...lines.map((l) => l.length), 4);
  const w = Math.max(drag?.[2] ?? 0, Math.min(360, longest * fontSize * 0.56 + 14));
  const wrapped = lines.reduce((n, l) => n + Math.max(1, Math.ceil((l.length * fontSize * 0.56) / Math.max(20, w - 14))), 0);
  const h = Math.max(drag?.[3] ?? 0, wrapped * fontSize * 1.2 + 12);
  return drag ? [drag[0], drag[1] + drag[3] - h, w, h] : [at[0], at[1] - h, w, h];
}

export const STAMP_PRESETS: Array<{ title: string; color: AppearanceColor }> = [
  { title: "APPROVED", color: "red" }, { title: "REJECTED", color: "red" }, { title: "REVISE & RESUBMIT", color: "orange" },
  { title: "FOR REVIEW", color: "blue" }, { title: "PRELIMINARY", color: "gray" },
];
export const DEFAULT_STAMP_SIZE: [number, number] = [140, 76];

export type StampChoice = { kind: "preset"; title: string; color: AppearanceColor } | { kind: "custom"; stamp: StampDefinition };

export function presetDetails(title: string, color: AppearanceColor, extra: { name?: string; date?: string; project?: string } = {}): ApprovalStampDetails {
  return {
    title, name: extra.name ?? "", date: extra.date ?? "", project: extra.project ?? "",
    style: { borderColor: color, borderStyle: "solid", borderWidth: 2, fillColor: "clear", textColor: color, font: "helveticaBold", fontSize: 11 },
  };
}

export function stampMarkup(pageId: string, layer: string, bounds: [number, number, number, number], details: ApprovalStampDetails): Markup {
  return { id: newItemId(), pageId, bounds, rotationDegrees: 0, layer, kind: "approvalStamp", style: details, createdAt: now() } as Markup;
}

/** A custom stamp's date/user lines are filled at placement, as Centerline does. */
export function customStampDetails(def: StampDefinition, user: string): ApprovalStampDetails {
  const d = structuredClone(def.details);
  if (d.custom) {
    if (d.custom.showDate && !d.date) {
      const fmt: Intl.DateTimeFormatOptions = d.custom.dateFormat === "short" ? { dateStyle: "short" } : d.custom.dateFormat === "long" ? { dateStyle: "long" } : { dateStyle: "medium" };
      d.date = new Intl.DateTimeFormat("en-US", fmt).format(new Date());
    }
    if (d.custom.showUser && !d.name) {
      const who = d.custom.userName || user;
      d.name = who ? `${d.custom.userPrefix ? `${d.custom.userPrefix} ` : ""}${who}` : "";
    }
  }
  return d;
}

export function newCustomStamp(input: { name: string; title: string; project: string; color: AppearanceColor; shape: "rectangle" | "roundedRectangle" | "doubleBorder" | "oval"; showDate: boolean; showUser: boolean; userName: string }): StampDefinition {
  const t = now();
  return {
    id: newItemId(), name: input.name.trim() || input.title, size: [180, 80], createdAt: t, updatedAt: t,
    details: {
      title: input.title.trim() || "STAMP", name: "", date: "", project: input.project.trim(),
      style: { borderColor: input.color, borderStyle: "solid", borderWidth: 2.5, fillColor: "white", textColor: input.color, font: "helveticaBold", fontSize: 12 },
      custom: { shape: input.shape, logoPosition: "left", showDate: input.showDate, dateFormat: "medium", showUser: input.showUser, userName: input.userName.trim(), userPrefix: "By", fillOpacity: 1 },
    },
  };
}

export function quickMeasure(pageId: string, layer: string, pts: Vec2[], closed: boolean, lengthUnit: QuickLengthUnit, areaUnit: QuickAreaUnit): Markup {
  return {
    id: newItemId(), pageId, bounds: pointsBox(pts), rotationDegrees: 0, layer, kind: "quickMeasure", createdAt: now(), vertices: pts,
    style: { closed, lengthUnit, areaUnit, color: "orange", labelRotationDegrees: 0, fontSize: 10 },
  } as Markup;
}

export const LINE_WIDTHS = [1, 2, 3, 5, 8] as const;

/** A straight Line: a Centerline quick-measure (so the Mac shows it as a distance) carrying the cloud `lineWidth`. */
export function lineMarkup(pageId: string, layer: string, a: Vec2, b: Vec2, color: AppearanceColor, lineWidth: number, lengthUnit: QuickLengthUnit): Markup {
  const pts: Vec2[] = [a, b];
  return {
    id: newItemId(), pageId, bounds: pointsBox(pts), rotationDegrees: 0, layer, kind: "quickMeasure", createdAt: now(), vertices: pts,
    style: { closed: false, lengthUnit, areaUnit: "SF", color, labelRotationDegrees: 0, fontSize: 10, lineWidth },
  } as Markup;
}

/** Shift-snap: turn `b` about `a` to the nearest 45°, keeping its length. */
export function snap45(a: Vec2, b: Vec2): Vec2 {
  const dx = b[0] - a[0], dy = b[1] - a[1], len = Math.hypot(dx, dy);
  if (!(len > 0)) return b;
  const ang = Math.round(Math.atan2(dy, dx) / (Math.PI / 4)) * (Math.PI / 4);
  const r = (v: number) => Math.round(v * 1e6) / 1e6;
  return [r(a[0] + Math.cos(ang) * len), r(a[1] + Math.sin(ang) * len)];
}

/** True for a plain Line markup (as opposed to a dashed quick measure). */
export const isLine = (m: Markup) => m.kind === "quickMeasure" && m.style.lineWidth != null && !m.style.closed;

export const SYMBOL_SHAPES = [
  { id: "triangle", label: "△", name: "Triangle" }, { id: "square", label: "□", name: "Square" }, { id: "circle", label: "○", name: "Circle" },
] as const;
export type SymbolShape = (typeof SYMBOL_SHAPES)[number]["id"];

/** An unlabeled Centerline labeled-shape (renders on the Mac as the same shape), centred on `at`. */
export function symbolMarkup(pageId: string, layer: string, shape: SymbolShape, at: Vec2, size: number, color: AppearanceColor, lineWidth: number): Markup {
  return {
    id: newItemId(), pageId, bounds: [at[0] - size / 2, at[1] - size / 2, size, size], rotationDegrees: 0, layer, kind: "shape", createdAt: now(),
    style: {
      shape, purpose: "general", label: "", strokeColor: color, fillColor: color, fillOpacity: 0, lineWidth, dashed: false,
      labelPlacement: "hidden", fontSize: 10, textColor: "ink", labelBackground: false, phase: "", notes: "",
    },
  } as Markup;
}

/** Resize a box about its centre to `size` × `size` (symbols keep a square box). */
export function resizedAboutCentre([x, y, w, h]: [number, number, number, number], size: number): [number, number, number, number] {
  const s = Math.max(4, Math.min(2000, size)), cx = x + w / 2, cy = y + h / 2;
  return [cx - s / 2, cy - s / 2, s, s];
}

export function takeoff(doc: CenterlineDocument, pageId: string, kind: "linear" | "area" | "count" | "arrow", pts: Vec2[], label: string): Measurement {
  return {
    id: newItemId(), kind, pageId, points: pts, label: label.trim(), layer: doc.layers.current, createdAt: now(), areaUnit: "SF", linearUnit: "LF",
  };
}

export function nextLabel(doc: CenterlineDocument, kind: "linear" | "area" | "count"): string {
  const base = kind === "linear" ? "Linear" : kind === "area" ? "Area" : "Count";
  return `${base} ${doc.measurements.filter((m) => m.kind === kind).length + 1}`;
}

export const COLOR_CHOICES: AppearanceColor[] = ["red", "orange", "yellow", "green", "teal", "blue", "navy", "purple", "gray", "ink"];

/** Recolours a markup the way Centerline's colour well does for that kind. */
export function recolor(m: Markup, c: AppearanceColor): Markup["style"] | null {
  const s = structuredClone(m.style) as any;
  switch (m.kind) {
    case "note": s.style.borderColor = c; s.style.textColor = c; return s;
    case "approvalStamp": case "signature": s.style.borderColor = c; s.style.textColor = c; return s;
    case "cloud": s.color = c; return s;
    case "highlight": s.color = c; return s;
    case "quickMeasure": s.color = c; return s;
    case "shape": s.strokeColor = c; s.fillColor = c; return s;
    case "workArea": s.fillColor = c; s.hatchColor = c; return s;
    default: return null;
  }
}

export const COMPLETED_LAYER = "Completed work";

export function cloudMarkup(pageId: string, layer: string, pts: Vec2[], color: AppearanceColor = "red"): Markup {
  return { id: newItemId(), pageId, bounds: pointsBox(pts), rotationDegrees: 0, layer, kind: "cloud", vertices: pts, createdAt: now(),
    style: { color, lineWidth: 1.5, arcRadius: 9, fillColor: "clear", fillOpacity: 0 } } as Markup;
}

export function highlightMarkup(pageId: string, layer: string, rect: [number, number, number, number], color: AppearanceColor = "green"): Markup {
  return { id: newItemId(), pageId, bounds: rect, rotationDegrees: 0, layer, kind: "highlight", createdAt: now(), style: { color, opacity: 0.35 } } as Markup;
}

export interface InkStroke { pts: Vec2[]; pressure: number[] }
const INK_RGB: Partial<Record<AppearanceColor, string>> = { red: "#d0312d", orange: "#ea7a12", yellow: "#f2c400", green: "#1d9a4a", teal: "#0f9a9a", blue: "#1f5fd1", navy: "#0f2a4f", purple: "#7b3fc4", gray: "#707780", ink: "#111111" };

/**
 * Freehand ink → a Centerline ink-only signature markup (renders on the Mac too): the strokes are
 * rasterized to a transparent PNG with pressure-varying width, and kept as vector paths as a fallback.
 * `base` is the line width in page points at mid pressure.
 */
export function inkMarkup(pageId: string, layer: string, strokes: InkStroke[], color: AppearanceColor, mode: "pen" | "marker"): Markup | null {
  const all = strokes.flatMap((s) => s.pts);
  if (all.length < 2) return null;
  const base = mode === "marker" ? 9 : 1.6;
  const pad = base * 1.6;
  const xs = all.map((p) => p[0]), ys = all.map((p) => p[1]);
  const x0 = Math.min(...xs) - pad, x1 = Math.max(...xs) + pad, y0 = Math.min(...ys) - pad, y1 = Math.max(...ys) + pad;
  const w = Math.max(1, x1 - x0), h = Math.max(1, y1 - y0);
  const k = Math.min(4, 2400 / Math.max(w, h));
  const c = document.createElement("canvas");
  c.width = Math.ceil(w * k); c.height = Math.ceil(h * k);
  const ctx = c.getContext("2d")!;
  ctx.lineCap = "round"; ctx.lineJoin = "round"; ctx.strokeStyle = INK_RGB[color] ?? "#d0312d";
  const X = (p: Vec2) => (p[0] - x0) * k, Y = (p: Vec2) => (y1 - p[1]) * k; // image rows top-down; page y is up
  for (const s of strokes) {
    for (let i = 1; i < s.pts.length; i++) {
      const pr = ((s.pressure[i - 1] ?? 0.5) + (s.pressure[i] ?? 0.5)) / 2;
      ctx.lineWidth = Math.max(0.5, base * (0.35 + pr * 1.3) * k);
      ctx.beginPath(); ctx.moveTo(X(s.pts[i - 1]!), Y(s.pts[i - 1]!)); ctx.lineTo(X(s.pts[i]!), Y(s.pts[i]!)); ctx.stroke();
    }
  }
  let png = c;
  if (mode === "marker") { // flat translucent (no darker overlaps)
    const o = document.createElement("canvas"); o.width = c.width; o.height = c.height;
    const octx = o.getContext("2d")!; octx.globalAlpha = 0.4; octx.drawImage(c, 0, 0); png = o;
  }
  const inkImageData = png.toDataURL("image/png").split(",")[1]!;
  const freehandPaths = strokes.map((s) => ({ points: s.pts.map((p) => [(p[0] - x0) / w, (y1 - p[1]) / h] as Vec2) }));
  return {
    id: newItemId(), pageId, bounds: [x0, y0, w, h], rotationDegrees: 0, layer, kind: "signature", createdAt: now(),
    style: {
      name: "", email: "", reason: "", location: "", includeTimestamp: false, timestamp: now(),
      style: { borderColor: "clear", borderStyle: "solid", borderWidth: 0, fillColor: "clear", textColor: color, font: "helvetica", fontSize: 10 },
      freehandPaths, inkOnly: true, inkImageData, inkAspectRatio: w / h, inkCaption: "",
    },
  } as Markup;
}
